import React from 'react';
import Kbd from './Kbd';

// One checklist row. Two shapes, chosen by `def.type`:
//   passfail — option buttons; once answered, collapses to a single toggle that
//              clears the answer on click (same affordance as before the reskin)
//   number   — a clamped −/+ stepper
//
// This row renders and reports; it never decides what a fail means. The two
// flows that use it disagree on that, deliberately:
//
//   Goods-in QC — a fail must NOT cascade. The supplier's RMA claim needs every
//                 defect enumerated, so the remaining checks stay answerable.
//   Bench test  — a fail auto-marks the rest N/A. The unit is going to
//                 quarantine regardless, so there is nothing to gain by
//                 walking the remaining checks.
//
// Both are correct for their flow, so `onAnswer` simply reports the value and
// the caller applies its own rule. Do not add cascade logic here.
//
// `focused` drives the roving-focus outline used by the arrow-key navigation in
// the parent; it is presentational only — the parent owns which row is active.
//
// `footer` renders inside the row, below the controls — bench test uses it for
// the per-check fail metadata and failure-notes textarea.
export default function CheckRow({
  def, value, index, total, focused, disabled,
  onAnswer, onClear, options = [], footer, minHeight = 66,
}) {
  const isFail = value === 'fail';
  const isAnswered = def.type === 'number'
    ? value != null && value !== ''
    : value === 'pass' || value === 'fail' || value === 'na';

  const num = parseInt(value, 10) || 0;

  return (
    <div
      data-check-row={def.id}
      style={{
        borderLeft: `3px solid ${isFail ? 'var(--danger-border)' : 'transparent'}`,
        borderBottom: index < total - 1 ? '1px solid var(--border-subtle)' : 'none',
        background: focused ? 'var(--surface-raised)' : 'transparent',
        boxShadow: focused ? 'inset 2px 0 0 0 var(--brand-default)' : 'none',
        transition: 'background 0.12s ease',
      }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 'var(--space-4)',
        padding: '0 var(--space-4)', minHeight,
      }}>
      <div className="tnum" style={{ width: 26, flexShrink: 0, fontSize: 12, color: 'var(--text-muted)' }}>
        {index + 1}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)' }}>{def.label}</div>
        {def.desc && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: '16px' }}>{def.desc}</div>
        )}
      </div>

      {def.type === 'number' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexShrink: 0 }}>
          <button type="button" className="btn btn-ghost" disabled={disabled}
            aria-label={`Decrease ${def.label}`}
            onClick={() => onAnswer(Math.max(0, num - 1))}
            style={{ padding: '3px 10px' }}>−</button>
          <span className="tnum" aria-live="polite"
            style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', minWidth: 24, textAlign: 'center' }}>
            {num}
          </span>
          <button type="button" className="btn btn-ghost" disabled={disabled}
            aria-label={`Increase ${def.label}`}
            onClick={() => onAnswer(num + 1)}
            style={{ padding: '3px 10px' }}>+</button>
        </div>
      ) : !isAnswered ? (
        <div role="group" aria-label={def.label} style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {options.map(o => (
            <button
              key={o.value} type="button" disabled={disabled}
              onClick={() => onAnswer(o.value)}
              aria-keyshortcuts={o.key}
              title={`${o.label} (${o.key})`}
              style={{
                height: 32, minWidth: 60, padding: '0 12px', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)', background: 'var(--surface-default)',
                color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: 14, fontWeight: 500,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}>
              <Kbd tone="ghost">{o.key}</Kbd>
              {o.label}
            </button>
          ))}
        </div>
      ) : (
        <button
          type="button" onClick={onClear} disabled={disabled}
          title="Click to change"
          aria-label={`${def.label}: ${value === 'pass' ? 'Passed' : value === 'fail' ? 'Failed' : 'Not applicable'}. Activate to change.`}
          style={{
            height: 32, minWidth: 60, padding: '0 14px', borderRadius: 'var(--radius-md)',
            fontWeight: 600, fontSize: 14, fontFamily: 'inherit',
            cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0,
            background: value === 'pass' ? 'var(--success-fill)' : value === 'fail' ? 'var(--danger-fill)' : 'var(--surface-inset)',
            color: value === 'pass' ? 'var(--success-text)' : value === 'fail' ? 'var(--danger-text)' : 'var(--text-muted)',
            border: `1px solid ${value === 'pass' ? 'var(--success-border)' : value === 'fail' ? 'var(--danger-border)' : 'var(--border-default)'}`,
          }}>
          {value === 'pass' ? '✓ Passed' : value === 'fail' ? '✗ Failed' : 'N/A'}
        </button>
      )}
      </div>
      {footer}
    </div>
  );
}
